import { useState, useEffect, useMemo } from "react";
import { BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, Treemap } from "recharts";
import DraggableModal from "./DraggableModal";
import SearchSelect from "./SearchSelect";
import { LOGO_B64 } from "./SalesReports";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId } from "./seasonFilter";
import ExportButton from "./ExportButton";
import { num2, intOr, s as xs } from "./xlsxExport";
import { loadFromSupabase } from "../supabaseDB";
import { calcLotProfit } from "./profitCalc";

var T = {
  en: {
    title: "Profit Tracker", subtitle: "Revenue, costs & margins by shipment",
    lot: "Lot", shipper: "Shipper", product: "Product", cases: "Cases", week: "Week", seasonCol: "Season",
    priceDiff: "Price Difference", commission: "Commission", feeMarkup: "Fee Markup", financeMarkup: "Finance", perBox: "$/Box",
    totalProfit: "Total Profit", margin: "Margin",
    grandPriceDiff: "Price Difference", grandCommission: "Commission", grandFeeMarkup: "Fee Markup",
    grandProfit: "Total Profit",
    salePrice: "Sale Price", reportedPrice: "Reported Price", spread: "Spread",
    summary: "Summary", byShipment: "By Shipment", byProduct: "By Product", byFee: "By Fee Type",
    all: "All", allShippers: "All shippers", allClients: "All clients", allProducts: "All products", allSeasons: "All seasons", allWeeks: "All weeks", allSaleStatuses: "All sale statuses",
    goodDelivery: "Good Delivery", protection: "Protection", pas: "PAS", dumped: "Dumped", donated: "Donated", samples: "Samples", transfer: "Transfer",
    noData: "No data yet. Create Bills and Invoices to see profit analysis.",
    feeName: "Fee", chargedToShipper: "Charged (Bill)", actualCost: "Actual Cost", feeProfit: "Markup Profit",
    category: "Category", allCategories: "All categories",
    catCustoms: "Customs", catWarehouse: "Warehouse", catQC: "Quality/Inspection", catShipping: "Shipping", catFinancing: "Financing", catCertification: "Certification", catOther: "Other",
    season: "Season",
    search: "Search...", breakdown: "Profit Breakdown",
    close: "Close",
  },
  es: {
    title: "Seguimiento de Ganancia", subtitle: "Ingresos, costos y márgenes por embarque",
    lot: "Lote", shipper: "Exportador", product: "Producto", cases: "Cajas", week: "Semana", seasonCol: "Temporada",
    priceDiff: "Diferencia de Precio", commission: "Comisión", feeMarkup: "Markup de Gastos", financeMarkup: "Financiamiento", perBox: "$/Caja",
    totalProfit: "Ganancia Total", margin: "Margen",
    grandPriceDiff: "Diferencia de Precio", grandCommission: "Comisión", grandFeeMarkup: "Markup de Gastos",
    grandProfit: "Ganancia Total",
    salePrice: "Precio Venta", reportedPrice: "Precio Reportado", spread: "Spread",
    summary: "Resumen", byShipment: "Por Embarque", byProduct: "Por Producto", byFee: "Por Gasto",
    all: "Todos", allShippers: "Todos los exportadores", allClients: "Todos", allProducts: "Todos", allSeasons: "Todas", allWeeks: "Todas", allSaleStatuses: "Todos los estados",
    goodDelivery: "Good Delivery", protection: "Protección", pas: "PAS", dumped: "Dumped", donated: "Donated", samples: "Samples", transfer: "Transferencia",
    noData: "Sin datos. Crea Bills y Facturas para ver el análisis de ganancia.",
    feeName: "Gasto", chargedToShipper: "Cobrado (Bill)", actualCost: "Costo Real", feeProfit: "Ganancia Markup",
    category: "Categoría", allCategories: "Todas",
    catCustoms: "Aduana", catWarehouse: "Bodega", catQC: "Calidad/Inspección", catShipping: "Envío", catFinancing: "Financiamiento", catCertification: "Certificación", catOther: "Otro",
    season: "Temporada",
    search: "Buscar...", breakdown: "Desglose de Ganancia",
    close: "Cerrar",
  },
};

function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch { return fb; } }
function fmtMoney(n) { var v = parseFloat(n) || 0; return (v < 0 ? "-$" : "$") + Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtPct(n) { return (parseFloat(n) || 0).toFixed(2) + "%"; }
function getISOWeek(dateStr) {
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00"); d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  var w1 = new Date(d.getFullYear(), 0, 4);
  return String(1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, "0");
}
/* Mirror of the canonical SALE_STATUSES list in BillRegistry — kept
   inline per the registry "no shared internals" convention. Used to
   populate the Sale Status filter dropdown. */
var SALE_STATUSES = ["goodDelivery", "protection", "pas", "dumped", "donated", "samples", "transfer"];

function getSeasons() { return loadLS("gfi-seasons", []); }
function detectSeason(dateStr) {
  if (!dateStr) return null;
  var seasons = getSeasons();
  var d = new Date(dateStr + "T00:00:00");
  return seasons.find(function(s) { return new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00"); });
}
function resolveShipperName(supplierId) {
  if (!supplierId) return "";
  var sups = loadLS("gfi-suppliers", []);
  var sup = sups.find(function(s) { return String(s.id) === String(supplierId); });
  return sup ? (sup.shortName || sup.fullName || sup.name || "") : "";
}

/* Per-lot profit math (priceDiff/commission/feeMarkup/finance/total/
   cases/revenue/margin/products/feeDetail, including the palletAllocations
   fix for split-lot-across-invoices double-counting) lives in
   profitCalc.js now — shared with BillRegistry.js's "My Profit" panel
   so there's exactly one place this math can go wrong, not 2+ copies
   drifting apart. buildProfitData() below only adds the metadata
   calcLotProfit doesn't know about: season/week assignment, shipDate,
   and sale-status severity, then maps the shared shape's field names
   onto this module's existing ones (financeMarkup/totalProfit/
   invoiceRevenue) so nothing downstream in this file has to change. */
function buildProfitData() {
  var bills = loadLS("gfi-bills", []);
  var invoices = loadLS("gfi-invoices", []);
  var shipments = loadLS("gfi-shipments", []);
  var shipmentByLot = {}; shipments.forEach(function(s) { if (s.lot) shipmentByLot[s.lot] = s; });

  var lotMap = {};
  bills.forEach(function(b) { var lot = b.lot; if (!lot) return; if (!lotMap[lot]) lotMap[lot] = { lot: lot, shipper: "", bills: [], invoices: [] }; lotMap[lot].shipper = b.shipperName || resolveShipperName(b.supplierId) || lotMap[lot].shipper; lotMap[lot].bills.push(b); });
  invoices.forEach(function(inv) {
    /* Support multi-lot invoices: index by line lot */
    var lots = new Set();
    (inv.lines || []).forEach(function(l) { if (l.lot) lots.add(l.lot); });
    if (lots.size === 0 && inv.lot) lots.add(inv.lot); /* fallback to global lot */
    lots.forEach(function(lot) { if (!lotMap[lot]) lotMap[lot] = { lot: lot, shipper: "", bills: [], invoices: [] }; lotMap[lot].invoices.push(inv); });
  });

  return Object.values(lotMap).map(function(entry) {
    var shp = shipmentByLot[entry.lot];
    var shipDate = shp ? (shp.dateArrival || shp.dateDeparture || "") : "";
    var weekNum = getISOWeek(shipDate);
    var season = detectSeason(shipDate);
    var seasonName = season ? (season.seasonName || season.startDate.substring(0, 4) + "-" + season.endEstimate.substring(0, 4)) : "";
    var seasonId = season ? String(season.id) : "";

    var lp = calcLotProfit(entry.lot);

    var worstStatus = ""; var allStatuses = [];
    entry.bills.forEach(function(b) { (b.lines || []).forEach(function(l) { if (l.saleStatus) allStatuses.push(l.saleStatus); }); });
    /* Severity: protection (quality) > pas (market) > goodDelivery. */
    if (allStatuses.includes("protection")) worstStatus = "protection"; else if (allStatuses.includes("pas")) worstStatus = "pas"; else if (allStatuses.includes("goodDelivery")) worstStatus = "goodDelivery";
    /* Distinct sale statuses across this lot's bill lines — used by
       the Sale Status filter in the toolbar. saleStatus (worst) is
       still the column display; allSaleStatuses captures the full set
       so the filter matches lots that contain the chosen status on
       ANY line, not only the worst one. */
    var distinctStatuses = []; var seenSt = {};
    allStatuses.forEach(function(s) { if (s && !seenSt[s]) { seenSt[s] = true; distinctStatuses.push(s); } });
    return { lot: entry.lot, shipper: entry.shipper, seasonName: seasonName, seasonId: seasonId, week: weekNum, shipDate: shipDate, saleStatus: worstStatus, allSaleStatuses: distinctStatuses, priceDiff: lp.priceDiff, commission: lp.commission, feeMarkup: lp.feeMarkup, financeMarkup: lp.finance, totalProfit: lp.total, margin: lp.margin, perBox: lp.perBox, cases: lp.cases, invoiceRevenue: lp.revenue, products: lp.products, billFeesByType: lp.billFeesByType, actualCostsByType: lp.actualCostsByType, feeDetail: lp.feeDetail };
  }).sort(function(a, b) { return (parseInt(b.lot, 10) || 0) - (parseInt(a.lot, 10) || 0); });
}


export default function ProfitTracker({ lang: globalLang }) {
  var lang = globalLang || "es"; var t = T[lang];
  var [data, setData] = useState([]);
  var [filterShippers, setFilterShippers] = useState([]); var [filterClients, setFilterClients] = useState([]); var [filterProducts, setFilterProducts] = useState([]); var [filterSaleStatus, setFilterSaleStatus] = useState("");
  /* Default to Open seasons on mount (CLAUDE.md "REGLA DE FILTRO DE SEASON"). */
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var [filterWeeks, setFilterWeeks] = useState([]);
  var [search, setSearch] = useState("");
  var [dateFrom, setDateFrom] = useState(""); var [dateTo, setDateTo] = useState("");
  var [sortCol, setSortCol] = useState("lot"); var [sortDir, setSortDir] = useState("desc");
  var [view, setView] = useState("summary");
  var [selectedLot, setSelectedLot] = useState(null);

  /* Pull every dataset ProfitTracker scans from Supabase before the
     calculation runs. Each fetch refreshes the legacy `gfi-*` cache so
     `buildProfitData()` (sync, reads localStorage) sees the latest. */
  useEffect(function() {
    var alive = true;
    var preload = function () {
      var pairs = [
        ["bills", "gfi-bills"],
        ["invoices", "gfi-invoices"],
        ["shipments", "gfi-shipments"],
        ["expense_bills", "gfi-expense-bills"],
        ["suppliers", "gfi-suppliers"],
        ["fee_catalog", "gfi-fee-catalog"],
        ["products", "gfi-products"],
        ["seasons", "gfi-seasons"],
      ];
      return Promise.all(pairs.map(function (p) {
        return loadFromSupabase(p[0]).then(function (rows) {
          try { localStorage.setItem(p[1], JSON.stringify(rows)); } catch (e) {}
        }).catch(function () { /* keep stale cache on failure */ });
      })).then(function () { if (alive) setData(buildProfitData()); });
    };
    preload();
    var r = function () { preload(); };
    window.addEventListener("focus", r);
    return function () { alive = false; window.removeEventListener("focus", r); };
  }, []);

  /* Client lookup keyed by lot — array form is needed before `filtered`
     so the multi-select Client filter can intersect against it. The
     string form (used by chart "Group By: Client") is derived. */
  var clientsByLot = useMemo(function () {
    var invs = loadLS("gfi-invoices", []);
    var m = {};
    invs.forEach(function (inv) {
      if (!inv.lot) return;
      if (!m[String(inv.lot)]) m[String(inv.lot)] = new Set();
      if (inv.clientName) m[String(inv.lot)].add(inv.clientName);
    });
    var out = {}; Object.keys(m).forEach(function (k) { out[k] = [...m[k]]; });
    return out;
  }, [data]);

  var filtered = useMemo(function() {
    var list = data;
    if (filterShippers.length > 0) list = list.filter(function(d) { return filterShippers.indexOf(d.shipper) >= 0; });
    if (filterSeasons.length > 0) list = list.filter(function(d) { return filterSeasons.indexOf(d.seasonId) >= 0; });
    if (filterWeeks.length > 0) list = list.filter(function(d) { return filterWeeks.indexOf(d.week) >= 0; });
    if (filterClients.length > 0) list = list.filter(function(d) {
      var lc = clientsByLot[String(d.lot)] || [];
      return lc.some(function (c) { return filterClients.indexOf(c) >= 0; });
    });
    if (filterProducts.length > 0) list = list.filter(function(d) {
      return (d.products || []).some(function (p) { return filterProducts.indexOf(p.product) >= 0; });
    });
    /* Sale status — matches lots whose distinct sale statuses (from
       ANY line of ANY bill) include the selected status. Falls back
       to the worst-only saleStatus for older rows that predate the
       allSaleStatuses field. */
    if (filterSaleStatus) list = list.filter(function(d) {
      var arr = d.allSaleStatuses && d.allSaleStatuses.length > 0 ? d.allSaleStatuses : (d.saleStatus ? [d.saleStatus] : []);
      return arr.indexOf(filterSaleStatus) >= 0;
    });
    if (search) { var s = search.toLowerCase(); list = list.filter(function(d) { return String(d.lot).includes(s) || (d.shipper || "").toLowerCase().includes(s); }); }
    if (dateFrom || dateTo) list = list.filter(function(d) { var dt = d.shipDate || ""; if (!dt) return false; if (dateFrom && dt < dateFrom) return false; if (dateTo && dt > dateTo) return false; return true; });
    return list;
  }, [data, filterShippers, filterClients, filterProducts, filterSaleStatus, filterSeasons, filterWeeks, search, dateFrom, dateTo, clientsByLot]);

  var sorted = useMemo(function() {
    return [...filtered].sort(function(a, b) {
      var va, vb;
      switch (sortCol) {
        case "lot": va = parseInt(a.lot, 10) || 0; vb = parseInt(b.lot, 10) || 0; return sortDir === "asc" ? va - vb : vb - va;
        case "shipper": va = a.shipper || ""; vb = b.shipper || ""; break;
        case "shipDate": va = a.shipDate || ""; vb = b.shipDate || ""; break;
        case "saleStatus": { var rk={protection:3,pas:2,goodDelivery:1}; va=rk[a.saleStatus]||0; vb=rk[b.saleStatus]||0; return sortDir==="asc"?va-vb:vb-va; }
        case "cases": va = a.cases; vb = b.cases; return sortDir === "asc" ? va - vb : vb - va;
        case "priceDiff": va = a.priceDiff; vb = b.priceDiff; return sortDir === "asc" ? va - vb : vb - va;
        case "commission": va = a.commission; vb = b.commission; return sortDir === "asc" ? va - vb : vb - va;
        case "feeMarkup": va = a.feeMarkup; vb = b.feeMarkup; return sortDir === "asc" ? va - vb : vb - va;
        case "financeMarkup": va = a.financeMarkup; vb = b.financeMarkup; return sortDir === "asc" ? va - vb : vb - va;
        case "totalProfit": va = a.totalProfit; vb = b.totalProfit; return sortDir === "asc" ? va - vb : vb - va;
        case "margin": va = a.margin; vb = b.margin; return sortDir === "asc" ? va - vb : vb - va;
        case "perBox": va = a.perBox; vb = b.perBox; return sortDir === "asc" ? va - vb : vb - va;
        case "week": va = a.week || ""; vb = b.week || ""; break;
        case "seasonName": va = a.seasonName || ""; vb = b.seasonName || ""; break;
        default: va = a[sortCol] || ""; vb = b[sortCol] || "";
      }
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
  }, [filtered, sortCol, sortDir]);

  var toggleSort = function(col) { if (sortCol === col) setSortDir(function(d) { return d === "asc" ? "desc" : "asc"; }); else { setSortCol(col); setSortDir(col === "lot" ? "desc" : "asc"); } };
  var arrow = function(col) { return sortCol === col ? (sortDir === "asc" ? " \u25b2" : " \u25bc") : ""; };

  var uniqueShippers = useMemo(function() { return [...new Set(data.map(function(d) { return d.shipper; }).filter(Boolean))].sort(); }, [data]);
  var uniqueClients = useMemo(function() { var s = new Set(); Object.values(clientsByLot).forEach(function(arr) { arr.forEach(function(c) { s.add(c); }); }); return [...s].sort(); }, [clientsByLot]);
  var uniqueProducts = useMemo(function() { var s = new Set(); data.forEach(function(d) { (d.products || []).forEach(function(p) { if (p.product && p.product !== "?") s.add(p.product); }); }); return [...s].sort(); }, [data]);
  var uniqueSeasons = useMemo(function() { return [...new Set(data.map(function(d) { return d.seasonName; }).filter(Boolean))].sort(); }, [data]);
  var uniqueWeeks = useMemo(function() { return [...new Set(data.map(function(d) { return d.week; }).filter(Boolean))].sort(); }, [data]);

  var totals = useMemo(function() {
    var r = { priceDiff: 0, commission: 0, feeMarkup: 0, financeMarkup: 0, profit: 0, revenue: 0, cases: 0, lots: filtered.length };
    filtered.forEach(function(d) { r.priceDiff += d.priceDiff; r.commission += d.commission; r.feeMarkup += d.feeMarkup; r.financeMarkup += d.financeMarkup; r.profit += d.totalProfit; r.revenue += d.invoiceRevenue; r.cases += d.cases; });
    r.margin = r.revenue > 0 ? (r.profit / r.revenue) * 100 : 0;
    r.perBox = r.cases > 0 ? r.profit / r.cases : 0;
    return r;
  }, [filtered]);

  /* Product aggregation */
  var productAgg = useMemo(function() {
    var map = {};
    filtered.forEach(function(d) { (d.products || []).forEach(function(p) {
      if (!map[p.product]) map[p.product] = { product: p.product, priceDiff: 0, count: 0 };
      map[p.product].priceDiff += p.priceDiff; map[p.product].count++;
    }); });
    return Object.values(map).sort(function(a, b) { return b.priceDiff - a.priceDiff; });
  }, [filtered]);

  /* Fee aggregation */
  var feeAgg = useMemo(function() {
    var map = {};
    filtered.forEach(function(d) {
      var allNames = new Set([...Object.keys(d.billFeesByType || {}), ...Object.keys(d.actualCostsByType || {})]);
      allNames.forEach(function(fname) { if (!map[fname]) map[fname] = { name: fname, charged: 0, actual: 0 }; map[fname].charged += (d.billFeesByType || {})[fname] || 0; map[fname].actual += (d.actualCostsByType || {})[fname] || 0; });
    });
    return Object.values(map);
  }, [filtered]);

  /* Map fee name → category by reading FeeCatalog (gfi-fee-catalog).
     Anything not in the catalog falls back to "other". The catalog is
     the single source of truth for category assignment system-wide. */
  var FEE_CATEGORY_KEYS = ["customs", "warehouse", "qc", "shipping", "financing", "certification", "other"];
  var FEE_CATEGORY_LABEL_KEYS = { customs: "catCustoms", warehouse: "catWarehouse", qc: "catQC", shipping: "catShipping", financing: "catFinancing", certification: "catCertification", other: "catOther" };
  function categoryLabel(c) { return t[FEE_CATEGORY_LABEL_KEYS[c]] || c || "—"; }
  var feeCategoryByName = useMemo(function() {
    var catalog = loadLS("gfi-fee-catalog", []);
    var m = {}; catalog.forEach(function(f) { if (f && f.name) m[f.name] = f.category || "other"; });
    return m;
  }, [data]);

  /* Independent sort + category filter for the "By Fee Type" tab.
     Default sort: profit desc (preserves the previous initial ordering). */
  var [feeSortCol, setFeeSortCol] = useState("profit");
  var [feeSortDir, setFeeSortDir] = useState("desc");
  var [feeCatFilter, setFeeCatFilter] = useState([]);
  var toggleFeeSort = function(col) { if (feeSortCol === col) setFeeSortDir(function(d) { return d === "asc" ? "desc" : "asc"; }); else { setFeeSortCol(col); setFeeSortDir(col === "name" || col === "category" ? "asc" : "desc"); } };
  var feeArrow = function(col) { return feeSortCol === col ? (feeSortDir === "asc" ? " ▲" : " ▼") : ""; };
  var feeSorted = useMemo(function() {
    var arr = feeAgg.map(function(f) { return { name: f.name, charged: f.charged, actual: f.actual, category: feeCategoryByName[f.name] || "other" }; });
    if (feeCatFilter.length > 0) arr = arr.filter(function(f) { return feeCatFilter.indexOf(f.category) >= 0; });
    arr.sort(function(a, b) {
      var va, vb;
      switch (feeSortCol) {
        case "category": va = categoryLabel(a.category); vb = categoryLabel(b.category); return feeSortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
        case "name": va = a.name || ""; vb = b.name || ""; return feeSortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
        case "charged": va = a.charged; vb = b.charged; break;
        case "actual": va = a.actual; vb = b.actual; break;
        case "profit": default: va = a.charged - a.actual; vb = b.charged - b.actual; break;
      }
      return feeSortDir === "asc" ? va - vb : vb - va;
    });
    return arr;
  }, [feeAgg, feeSortCol, feeSortDir, feeCatFilter, feeCategoryByName, lang]);

  /* ───────────── CHART CONTROLS ─────────────
     Independent chart panel that re-aggregates the same `filtered` set
     used by the tables, so every existing filter (shipper/season/week/
     date range/search) flows into the chart. The metric × groupBy ×
     compareMode trio gives a small grid of permutations the user can
     explore. */
  var [chartMetric, setChartMetric] = useState("Profit");
  var [chartGroup, setChartGroup] = useState("Shipper");
  var [chartCompare, setChartCompare] = useState("none");
  var [chartType, setChartType] = useState("bar");
  var [compareA, setCompareA] = useState("");
  var [compareB, setCompareB] = useState("");

  /* Stable palette for known commodities; everything else gets an
     auto-assigned color from PALETTE. */
  var COMMODITY_COLORS = { Blackberry: "#4A0E4E", Blueberry: "#1B4F72", Raspberry: "#C0392B", Strawberry: "#E11D48", Avocado: "#15803D", Lime: "#65A30D" };
  var PALETTE = ["#1B4F72", "#4A0E4E", "#C0392B", "#15803D", "#B45309", "#7C3AED", "#0891B2", "#DC2626", "#EA580C", "#16A34A", "#0E7490", "#9333EA"];
  function colorForKey(key, idx) { return COMMODITY_COLORS[key] || PALETTE[idx % PALETTE.length]; }

  /* Reads bills + invoices for the lots present in `filtered` and unrolls
     them by product/pack-size. Returns rows like
     { lot, product, packSize, shipper, seasonName, week, shipDate, cases,
       revenue, profit, perBox, commission, ... } — one per (lot × product)
     pair. Used by Product / Pack Size groupings. */
  var productRows = useMemo(function () {
    var allBills = loadLS("gfi-bills", []);
    var allInvoices = loadLS("gfi-invoices", []);
    var allProducts = loadLS("gfi-products", []);
    var prodById = {}; allProducts.forEach(function (p) { prodById[String(p.id)] = p; });
    function packSizeFor(line) {
      if (line.size) return line.size;
      if (line.productId && prodById[String(line.productId)]) return prodById[String(line.productId)].size || "—";
      /* Fallback: pull "6oz" / "12oz" / "1pt" / "1lb" / "2lb" from the
         description string. */
      var desc = String(line.productDescription || "");
      var m = desc.match(/(\d+\s*(?:oz|pt|lb|kg))/i);
      return m ? m[1].toLowerCase().replace(/\s+/g, "") : "—";
    }
    var byLot = {}; filtered.forEach(function (r) { byLot[String(r.lot)] = r; });
    var out = [];
    allBills.forEach(function (b) {
      var r = byLot[String(b.lot)]; if (!r) return;
      var lotCases = 0;
      (b.lines || []).forEach(function (l) { lotCases += parseInt(l.cases, 10) || 0; });
      (b.lines || []).forEach(function (l) {
        var c = parseInt(l.cases, 10) || 0;
        var w = lotCases > 0 ? c / lotCases : 0;
        var product = l.productName || l.productBase || "?";
        /* Drop product lines outside the active commodity selection so
           "Group By: Product / Pack Size" charts only show selected
           commodities. Lot-level filter (in `filtered`) keeps the lot
           if it contains ANY selected product; this trims the rest. */
        if (filterProducts.length > 0 && filterProducts.indexOf(product) < 0) return;
        out.push({
          lot: r.lot, product: product, packSize: packSizeFor(l),
          shipper: r.shipper, seasonName: r.seasonName, week: r.week, shipDate: r.shipDate,
          cases: c, revenue: r.invoiceRevenue * w, profit: r.totalProfit * w,
          commission: r.commission * w, feeMarkup: r.feeMarkup * w, financeMarkup: r.financeMarkup * w,
        });
      });
    });
    /* For each row+invoice, override revenue with per-line sale amount
       since that's more accurate than the proportional split. */
    return out;
  }, [filtered, filterProducts]);

  /* Comma-joined client names per lot — used by the chart "Group By:
     Client" bucket. Derived from the array form (clientsByLot) hoisted
     above `filtered`. */
  var clientByLot = useMemo(function () {
    var out = {}; Object.keys(clientsByLot).forEach(function (k) { out[k] = clientsByLot[k].join(", "); });
    return out;
  }, [clientsByLot]);

  /* Computes the metric for one record. For non-weighted metrics it sums.
     Margin and Profit-per-Box are derived from totals during aggregation. */
  function pickMetric(rec, m) {
    switch (m) {
      case "Cases": return rec.cases || 0;
      case "Sales Amount": return rec.revenue !== undefined ? rec.revenue : (rec.invoiceRevenue || 0);
      case "Profit": return rec.profit !== undefined ? rec.profit : (rec.totalProfit || 0);
      case "Commission": return rec.commission || 0;
      case "Fee Markup": return rec.feeMarkup || 0;
      case "Finance": return rec.financeMarkup || 0;
      default: return 0;
    }
  }

  /* groupKey returns the bucket name for a record under the chosen group. */
  function groupKeyOf(rec, g) {
    switch (g) {
      case "Product": return rec.product || "—";
      case "Pack Size": return rec.packSize || "—";
      case "Shipper": return rec.shipper || "—";
      case "Client": return clientByLot[String(rec.lot)] || "—";
      case "Season": return rec.seasonName || "—";
      case "Week": return rec.week ? "W" + rec.week : "—";
      case "Month": return rec.shipDate ? rec.shipDate.substring(0, 7) : "—";
      default: return "—";
    }
  }

  /* Decides which dataset feeds the chart: product-level rows when the
     group is Product or Pack Size; lot-level rows otherwise. */
  var chartSource = useMemo(function () {
    return (chartGroup === "Product" || chartGroup === "Pack Size") ? productRows : filtered;
  }, [chartGroup, productRows, filtered]);

  /* Builds aggregated points. For weighted metrics (Margin %, Profit per
     Box) we accumulate numerator and denominator separately and divide at
     the end. */
  function aggregate(records, metric, groupBy) {
    var buckets = {};
    records.forEach(function (rec) {
      var k = groupKeyOf(rec, groupBy);
      if (!buckets[k]) buckets[k] = { key: k, value: 0, profit: 0, revenue: 0, cases: 0 };
      var bucket = buckets[k];
      bucket.profit += rec.profit !== undefined ? rec.profit : (rec.totalProfit || 0);
      bucket.revenue += rec.revenue !== undefined ? rec.revenue : (rec.invoiceRevenue || 0);
      bucket.cases += rec.cases || 0;
      if (metric !== "Margin %" && metric !== "Profit per Box") {
        bucket.value += pickMetric(rec, metric);
      }
    });
    var arr = Object.values(buckets).map(function (b) {
      if (metric === "Margin %") b.value = b.revenue > 0 ? (b.profit / b.revenue) * 100 : 0;
      else if (metric === "Profit per Box") b.value = b.cases > 0 ? b.profit / b.cases : 0;
      return b;
    });
    arr.sort(function (a, b) { return b.value - a.value; });
    return arr;
  }

  var chartData = useMemo(function () { return aggregate(chartSource, chartMetric, chartGroup); }, [chartSource, chartMetric, chartGroup, clientByLot]);

  /* Available comparison keys derived from the data. */
  var compareOptions = useMemo(function () {
    if (chartCompare === "none") return [];
    var s = new Set();
    filtered.forEach(function (r) {
      if (chartCompare === "week" && r.week) s.add((r.seasonName || "—") + " · W" + r.week);
      else if (chartCompare === "month" && r.shipDate) s.add(r.shipDate.substring(0, 7));
      else if (chartCompare === "season" && r.seasonName) s.add(r.seasonName);
    });
    return [...s].sort();
  }, [filtered, chartCompare]);

  /* When compare mode is active, build TWO datasets filtered by the
     selected keys and merge them so the chart can render side-by-side
     bars or two lines. */
  var compareData = useMemo(function () {
    if (chartCompare === "none" || !compareA || !compareB) return null;
    function matches(rec, key) {
      if (chartCompare === "week") return ((rec.seasonName || "—") + " · W" + rec.week) === key;
      if (chartCompare === "month") return rec.shipDate && rec.shipDate.substring(0, 7) === key;
      if (chartCompare === "season") return (rec.seasonName || "—") === key;
      return false;
    }
    var dataA = aggregate(chartSource.filter(function (r) { return matches(r, compareA); }), chartMetric, chartGroup);
    var dataB = aggregate(chartSource.filter(function (r) { return matches(r, compareB); }), chartMetric, chartGroup);
    var keys = new Set([...dataA.map(function (d) { return d.key; }), ...dataB.map(function (d) { return d.key; })]);
    var mapA = {}; dataA.forEach(function (d) { mapA[d.key] = d.value; });
    var mapB = {}; dataB.forEach(function (d) { mapB[d.key] = d.value; });
    return [...keys].map(function (k) { return { key: k, A: mapA[k] || 0, B: mapB[k] || 0 }; }).sort(function (x, y) { return (y.A + y.B) - (x.A + x.B); });
  }, [chartCompare, compareA, compareB, chartSource, chartMetric, chartGroup, clientByLot]);

  function fmtVal(v) {
    if (chartMetric === "Margin %") return (v || 0).toFixed(1) + "%";
    if (chartMetric === "Cases") return (v || 0).toLocaleString("en-US");
    return fmtMoney(v);
  }

  /* Captures the chart container's outer HTML and wraps it in a print
     doc with the Ground Fresh brand. Recharts renders into SVG so the
     HTML serializes cleanly. Active filters become the subtitle. */
  function printChart() {
    var node = document.getElementById("pt-chart-container");
    if (!node) return;
    var svgHtml = node.innerHTML;
    var filtersList = [];
    if (filterShippers.length > 0) filtersList.push(filterShippers.join(", "));
    if (filterClients.length > 0) filtersList.push(filterClients.join(", "));
    if (filterProducts.length > 0) filtersList.push(filterProducts.join(", "));
    if (filterSeasons.length > 0) filtersList.push(filterSeasons.join(", "));
    if (filterWeeks.length > 0) filtersList.push(filterWeeks.map(function (w) { return "W" + w; }).join(", "));
    if (dateFrom || dateTo) filtersList.push((dateFrom || "…") + " → " + (dateTo || "…"));
    if (search) filtersList.push("\"" + search + "\"");
    var filtersLine = filtersList.length > 0 ? filtersList.join(" · ") : (lang === "es" ? "Todos los datos" : "All data");
    var compareLine = (chartCompare !== "none" && compareA && compareB) ? (compareA + "  vs  " + compareB) : "";
    var today = new Date().toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { year: "numeric", month: "long", day: "numeric" });
    var doc = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Profit Tracker</title><style>';
    doc += '@page{size:letter landscape;margin:0.5in}';
    doc += '*{margin:0;padding:0;box-sizing:border-box}';
    doc += 'body{font-family:"Segoe UI",Arial,sans-serif;color:#1a1a1a;line-height:1.4}';
    doc += '.page{max-width:1000px;margin:0 auto;padding:8px}';
    doc += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:14px;border-bottom:3px solid #1B3A4B;margin-bottom:14px}';
    /* Bumped from 64 → 95 — matches other GFI docs so "International" reads. */
    doc += '.logo img{height:95px}';
    doc += '.doc-title{font-size:22px;font-weight:300;color:#1B3A4B;letter-spacing:1.5px;text-align:right}';
    doc += '.doc-sub{font-size:11px;color:#666;text-align:right;margin-top:4px}';
    doc += '.filters{font-size:11px;color:#666;text-align:right;margin-top:2px}';
    doc += '.compare{font-size:11px;color:#1B3A4B;font-weight:700;text-align:right;margin-top:2px}';
    doc += '.chart-wrap{padding:14px;border:1px solid #e0e0e0;border-radius:8px;background:#fff;margin-bottom:14px}';
    doc += '.chart-wrap svg{width:100%;height:auto;max-height:520px}';
    doc += '.footer{margin-top:18px;padding-top:10px;border-top:1px solid #ddd;font-size:9px;color:#999;text-align:center}';
    doc += '</style></head><body><div class="page">';
    doc += '<div class="header"><div class="logo"><img src="' + LOGO_B64 + '" /></div>';
    doc += '<div><div class="doc-title">PROFIT TRACKER</div>';
    doc += '<div class="doc-sub">' + chartMetric + ' ' + (lang === "es" ? "por" : "by") + ' ' + chartGroup + ' · ' + today + '</div>';
    doc += '<div class="filters">' + filtersLine + '</div>';
    if (compareLine) doc += '<div class="compare">' + compareLine + '</div>';
    doc += '</div></div>';
    doc += '<div class="chart-wrap">' + svgHtml + '</div>';
    doc += '<div class="footer">Ground Fresh International, Inc. · Lindenhurst, NY · ground-fresh.com</div>';
    doc += '</div></body></html>';
    var w = window.open("", "_blank");
    if (!w) return;
    w.document.open(); w.document.write(doc); w.document.close();
    setTimeout(function () { try { w.focus(); w.print(); } catch (e) {} }, 600);
  }

  var inp = { padding: "7px 8px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
  var card = { padding: "10px 12px", borderRadius: 8, background: "var(--color-bg)", textAlign: "center" };
  var th = { padding: "10px 8px", textAlign: "left", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", borderBottom: "2px solid var(--color-border)" };
  var clr = function(v) { return v > 0 ? "#059669" : v < 0 ? "#DC2626" : "var(--color-text-muted)"; };

  if (data.length === 0) return <div><h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 6px" }}>{t.title}</h2><p style={{ color: "var(--color-text-muted)", fontSize: 13 }}>{t.noData}</p></div>;

  /* Excel export — single sheet (per user choice).
     Summary  = one row per shipment (lot) with the same totals shown
                in the By Shipment table.
     Detail   = a long-form union of every By-Shipment, By-Product and
                By-Fee-Type row in the same sheet, with a Type column
                discriminating which view the row came from. Lets the
                user pivot one workbook covering all four PT tabs. */
  var buildPtSummaryRows = function () {
    return filtered.map(function (d) {
      var clients = clientsByLot[String(d.lot)] || [];
      return {
        Lot: xs(d.lot),
        Date: xs(d.date),
        Week: intOr(d.week),
        Season: xs(d.seasonName),
        Shipper: xs(d.shipper),
        Clients: clients.join(", "),
        Cases: intOr(d.cases),
        Revenue: num2(d.invoiceRevenue),
        "Price Diff": num2(d.priceDiff),
        Commission: num2(d.commission),
        "Fee Markup": num2(d.feeMarkup),
        "Finance Markup": num2(d.financeMarkup),
        Profit: num2(d.totalProfit),
        "Profit / Box": num2(d.cases > 0 ? d.totalProfit / d.cases : 0),
        Margin: num2(d.invoiceRevenue > 0 ? (d.totalProfit / d.invoiceRevenue) * 100 : 0),
      };
    });
  };
  var buildPtDetailRows = function () {
    var out = [];
    /* By Shipment rows */
    filtered.forEach(function (d) {
      out.push({
        Type: "Shipment",
        Lot: xs(d.lot), Date: xs(d.date), Week: intOr(d.week), Season: xs(d.seasonName),
        Shipper: xs(d.shipper), Product: "", Fee: "", Category: "",
        Cases: intOr(d.cases),
        Revenue: num2(d.invoiceRevenue),
        Charged: "", Actual: "",
        Amount: num2(d.totalProfit),
      });
      /* Per-product rows for the same lot */
      (d.products || []).forEach(function (p) {
        out.push({
          Type: "Product",
          Lot: xs(d.lot), Date: xs(d.date), Week: intOr(d.week), Season: xs(d.seasonName),
          Shipper: xs(d.shipper), Product: xs(p.product), Fee: "", Category: "",
          Cases: intOr(p.cases), Revenue: "",
          Charged: "", Actual: "",
          Amount: num2(p.priceDiff),
        });
      });
    });
    /* By Fee Type rows (already aggregated across `filtered`) */
    feeSorted.forEach(function (f) {
      out.push({
        Type: "FeeType",
        Lot: "", Date: "", Week: "", Season: "",
        Shipper: "", Product: "", Fee: xs(f.name), Category: xs(categoryLabel(f.category)),
        Cases: "", Revenue: "",
        Charged: num2(f.charged), Actual: num2(f.actual),
        Amount: num2(f.charged - f.actual),
      });
    });
    return out;
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div><h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{t.title}</h2><span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{t.subtitle}</span></div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <ExportButton lang={lang} filenameBase="ProfitTracker" totalCount={filtered.length} buildSummary={buildPtSummaryRows} buildDetail={buildPtDetailRows} />
          <div style={{ display: "flex", gap: 4 }}>
            {["summary", "byShipment", "byProduct", "byFee"].map(function(v) {
              return <button key={v} onClick={function() { setView(v); }} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid " + (view === v ? "var(--color-accent)" : "var(--color-border)"), background: view === v ? "rgba(42,122,111,0.08)" : "transparent", color: view === v ? "var(--color-accent)" : "var(--color-text-muted)", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{t[v]}</button>;
            })}
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <input value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder={t.search} style={{ ...inp, maxWidth: 180 }} />
        <div style={{ maxWidth: 180, flex: "1 1 160px" }}><SearchSelect multi value={filterShippers} onChange={v => setFilterShippers(v)} placeholder={t.allShippers} options={uniqueShippers.map(function(s) { return { value: s, label: s }; })} /></div>
        <div style={{ maxWidth: 180, flex: "1 1 160px" }}><SearchSelect multi value={filterClients} onChange={v => setFilterClients(v)} placeholder={t.allClients} options={uniqueClients.map(function(c) { return { value: c, label: c }; })} /></div>
        <div style={{ maxWidth: 180, flex: "1 1 160px" }}><SearchSelect multi value={filterProducts} onChange={v => setFilterProducts(v)} placeholder={t.allProducts} options={uniqueProducts.map(function(p) { return { value: p, label: p }; })} /></div>
        <div style={{ maxWidth: 200, flex: "1 1 180px" }}><SearchSelect multi value={filterSeasons} onChange={v => setFilterSeasons(v)} placeholder={seasonFilterPlaceholder(lang)} groups={buildSeasonFilterOptions(lang)} /></div>
        <div style={{ maxWidth: 130, flex: "1 1 110px" }}><SearchSelect multi value={filterWeeks} onChange={v => setFilterWeeks(v)} placeholder={t.allWeeks} options={uniqueWeeks.map(function(w) { return { value: w, label: "W" + w }; })} /></div>
        <div style={{ maxWidth: 160, flex: "1 1 140px" }}><SearchSelect value={filterSaleStatus} onChange={v => setFilterSaleStatus(v)} placeholder={t.allSaleStatuses} options={SALE_STATUSES.map(function(s) { return { value: s, label: t[s] || s }; })} /></div>
        <input type="date" value={dateFrom} onChange={function(e) { setDateFrom(e.target.value); }} title={lang === "es" ? "Desde" : "From"} style={{ ...inp, maxWidth: 140 }} />
        <input type="date" value={dateTo} onChange={function(e) { setDateTo(e.target.value); }} title={lang === "es" ? "Hasta" : "To"} style={{ ...inp, maxWidth: 140 }} />
        {(dateFrom || dateTo) && <button onClick={function() { setDateFrom(""); setDateTo(""); }} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>}
      </div>

      {/* ───────────── DYNAMIC CHART PANEL ─────────────
          Visible only in the Summary view; the other tabs (By Shipment /
          By Product / By Fee Type) render their tables full-width with no
          chart above them. Mirrors the same `filtered` set used by the
          tables so toggling filters updates the chart immediately. */}
      {view === "summary" && (
      <div style={{ marginBottom: 16, border: "1px solid var(--color-border)", borderRadius: 10, background: "var(--color-bg-card-solid)", overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg)" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{lang === "es" ? "Métrica" : "Metric"}</span>
            <div style={{ minWidth: 140 }}><SearchSelect value={chartMetric} onChange={v => setChartMetric(v)} options={["Cases", "Sales Amount", "Profit", "Profit per Box", "Margin %", "Commission", "Fee Markup", "Finance"].map(function(m) { return { value: m, label: m }; })} /></div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{lang === "es" ? "Agrupar por" : "Group By"}</span>
            <div style={{ minWidth: 130 }}><SearchSelect value={chartGroup} onChange={v => setChartGroup(v)} options={["Product", "Pack Size", "Shipper", "Client", "Season", "Week", "Month"].map(function(g) { return { value: g, label: g }; })} /></div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{lang === "es" ? "Comparar" : "Compare"}</span>
            <div style={{ minWidth: 130 }}><SearchSelect value={chartCompare} onChange={v => { setChartCompare(v); setCompareA(""); setCompareB(""); }} options={[{ value: "none", label: lang === "es" ? "Ninguno" : "None" }, { value: "week", label: "Week vs Week" }, { value: "month", label: "Month vs Month" }, { value: "season", label: "Season vs Season" }]} /></div>
          </div>
          {chartCompare !== "none" && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>A</span>
                <div style={{ minWidth: 150 }}><SearchSelect value={compareA} onChange={v => setCompareA(v)} placeholder="—" options={compareOptions.map(function(o) { return { value: o, label: o }; })} /></div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>B</span>
                <div style={{ minWidth: 150 }}><SearchSelect value={compareB} onChange={v => setCompareB(v)} placeholder="—" options={compareOptions.map(function(o) { return { value: o, label: o }; })} /></div>
              </div>
            </>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{lang === "es" ? "Tipo" : "Chart Type"}</span>
            <div style={{ minWidth: 160 }}><SearchSelect value={chartType} onChange={v => setChartType(v)} options={[{ value: "bar", label: "Bar Chart (vertical)" }, { value: "barH", label: "Bar Chart (horizontal)" }, { value: "barStacked", label: "Stacked Bar Chart" }, { value: "line", label: "Line Chart" }, { value: "area", label: "Area Chart" }, { value: "pie", label: "Pie Chart" }, { value: "donut", label: "Donut Chart" }, { value: "treemap", label: "Treemap" }]} /></div>
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={printChart} title={lang === "es" ? "Imprimir Gráfico" : "Print Chart"} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid #1B3A4B", background: "#1B3A4B", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700, alignSelf: "flex-end" }}>{"🖨 "}{lang === "es" ? "Imprimir" : "Print Chart"}</button>
        </div>
        <div id="pt-chart-container" style={{ padding: 14, height: 480, background: "#fff" }}>
          {(function() {
            if (chartCompare !== "none" && compareA && compareB && compareData) {
              /* Two-series comparison: bar (any horizontal/vertical variant)
                 or line. Pie/Treemap fall back to bar when compare is on. */
              var typeForCompare = (chartType === "line" || chartType === "area") ? chartType : "bar";
              if (typeForCompare === "line") {
                return (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={compareData} margin={{ top: 10, right: 20, left: 10, bottom: 90 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="key" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" interval={0} height={80} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtVal} />
                      <Tooltip formatter={fmtVal} />
                      <Legend />
                      <Line type="monotone" dataKey="A" name={compareA} stroke={PALETTE[0]} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="B" name={compareB} stroke={PALETTE[2]} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                );
              }
              if (typeForCompare === "area") {
                return (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={compareData} margin={{ top: 10, right: 20, left: 10, bottom: 90 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="key" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" interval={0} height={80} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtVal} />
                      <Tooltip formatter={fmtVal} />
                      <Legend />
                      <Area type="monotone" dataKey="A" name={compareA} stroke={PALETTE[0]} fill={PALETTE[0]} fillOpacity={0.4} />
                      <Area type="monotone" dataKey="B" name={compareB} stroke={PALETTE[2]} fill={PALETTE[2]} fillOpacity={0.4} />
                    </AreaChart>
                  </ResponsiveContainer>
                );
              }
              return (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={compareData} layout={chartType === "barH" ? "vertical" : "horizontal"} margin={{ top: 10, right: 20, left: 10, bottom: 90 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    {chartType === "barH" ? <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={fmtVal} /> : <XAxis dataKey="key" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" interval={0} height={80} />}
                    {chartType === "barH" ? <YAxis dataKey="key" type="category" tick={{ fontSize: 10 }} width={120} /> : <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtVal} />}
                    <Tooltip formatter={fmtVal} />
                    <Legend />
                    <Bar dataKey="A" name={compareA} fill={PALETTE[0]} radius={[3, 3, 0, 0]} stackId={chartType === "barStacked" ? "s" : undefined} />
                    <Bar dataKey="B" name={compareB} fill={PALETTE[2]} radius={[3, 3, 0, 0]} stackId={chartType === "barStacked" ? "s" : undefined} />
                  </BarChart>
                </ResponsiveContainer>
              );
            }
            /* No comparison — single series. */
            if (chartData.length === 0) return <div style={{ textAlign: "center", padding: 40, color: "#999", fontSize: 12 }}>{lang === "es" ? "Sin datos para esta combinación" : "No data for this combination"}</div>;
            if (chartType === "pie" || chartType === "donut") {
              return (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={chartData} dataKey="value" nameKey="key" cx="50%" cy="50%" outerRadius={130} innerRadius={chartType === "donut" ? 60 : 0} label={function(e) { return e.key; }} labelLine={false}>
                      {chartData.map(function (d, i) { return <Cell key={d.key} fill={colorForKey(d.key, i)} />; })}
                    </Pie>
                    <Tooltip formatter={fmtVal} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              );
            }
            if (chartType === "line") {
              return (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 90 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="key" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" interval={0} height={80} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtVal} />
                    <Tooltip formatter={fmtVal} />
                    <Line type="monotone" dataKey="value" stroke={PALETTE[0]} strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              );
            }
            if (chartType === "area") {
              return (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 90 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="key" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" interval={0} height={80} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtVal} />
                    <Tooltip formatter={fmtVal} />
                    <Area type="monotone" dataKey="value" stroke={PALETTE[0]} fill={PALETTE[0]} fillOpacity={0.4} />
                  </AreaChart>
                </ResponsiveContainer>
              );
            }
            if (chartType === "treemap") {
              return (
                <ResponsiveContainer width="100%" height="100%">
                  <Treemap data={chartData.map(function(d, i) { return { name: d.key, size: Math.max(0, d.value), fill: colorForKey(d.key, i) }; })} dataKey="size" stroke="#fff" />
                </ResponsiveContainer>
              );
            }
            /* bar (vertical), barH, barStacked single-series — stacked falls
               back to plain bar with one series. */
            return (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout={chartType === "barH" ? "vertical" : "horizontal"} margin={{ top: 10, right: 20, left: 10, bottom: 90 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  {chartType === "barH" ? <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={fmtVal} /> : <XAxis dataKey="key" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" interval={0} height={80} />}
                  {chartType === "barH" ? <YAxis dataKey="key" type="category" tick={{ fontSize: 10 }} width={120} /> : <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtVal} />}
                  <Tooltip formatter={fmtVal} />
                  <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                    {chartData.map(function (d, i) { return <Cell key={d.key} fill={colorForKey(d.key, i)} />; })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            );
          })()}
        </div>
      </div>
      )}

      {/* SUMMARY — Executive Dashboard */}
      {view === "summary" && (
        <div>
          {/* Hero card */}
          <div style={{ padding: "12px 18px", borderRadius: 10, background: "linear-gradient(135deg, rgba(42,122,111,0.08) 0%, rgba(42,122,111,0.02) 100%)", border: "1px solid rgba(42,122,111,0.15)", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-muted)", fontWeight: 700, marginBottom: 3 }}>{t.grandProfit}</div>
              <div style={{ fontSize: 24, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: clr(totals.profit), lineHeight: 1 }}>{fmtMoney(totals.profit)}</div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3 }}>{totals.lots} {lang === "es" ? "embarques" : "shipments"} \u00b7 {totals.cases.toLocaleString()} {lang === "es" ? "cajas" : "cases"}</div>
            </div>
            <div style={{ display: "flex", gap: 16 }}>
              <div style={{ textAlign: "center" }}><div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)" }}>{fmtPct(totals.margin)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)", fontWeight: 600, marginTop: 1 }}>{t.margin}</div></div>
              <div style={{ textAlign: "center" }}><div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)" }}>{fmtMoney(totals.perBox)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)", fontWeight: 600, marginTop: 1 }}>{t.perBox}</div></div>
            </div>
          </div>
          {/* Breakdown cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            <div style={card}><div style={{ fontSize: 14, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: clr(totals.priceDiff) }}>{fmtMoney(totals.priceDiff)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)", fontWeight: 600, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.grandPriceDiff}</div></div>
            <div style={card}><div style={{ fontSize: 14, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "#059669" }}>{fmtMoney(totals.commission)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)", fontWeight: 600, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.grandCommission}</div></div>
            <div style={card}><div style={{ fontSize: 14, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: clr(totals.feeMarkup) }}>{fmtMoney(totals.feeMarkup)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)", fontWeight: 600, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.grandFeeMarkup}</div></div>
            <div style={card}><div style={{ fontSize: 14, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: clr(totals.financeMarkup) }}>{fmtMoney(totals.financeMarkup)}</div><div style={{ fontSize: 8, color: "var(--color-text-muted)", fontWeight: 600, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.financeMarkup}</div></div>
          </div>
        </div>
      )}

      {/* BY SHIPMENT */}
      {view === "byShipment" && (
        <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid var(--color-border)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th onClick={function() { toggleSort("lot"); }} style={{ ...th, textAlign: "center" }}>{t.lot}{arrow("lot")}</th>
              <th onClick={function() { toggleSort("shipDate"); }} style={th}>Date{arrow("shipDate")}</th>
              <th onClick={function() { toggleSort("shipper"); }} style={th}>{t.shipper}{arrow("shipper")}</th>
              <th onClick={function() { toggleSort("cases"); }} style={{ ...th, textAlign: "right" }}>{t.cases}{arrow("cases")}</th>
              <th onClick={function() { toggleSort("week"); }} style={{ ...th, textAlign: "center" }}>{t.week}{arrow("week")}</th>
              <th onClick={function() { toggleSort("seasonName"); }} style={th}>{t.seasonCol}{arrow("seasonName")}</th>
              <th onClick={function() { toggleSort("priceDiff"); }} style={{ ...th, textAlign: "right" }}>{t.priceDiff}{arrow("priceDiff")}</th>
              <th onClick={function() { toggleSort("commission"); }} style={{ ...th, textAlign: "right" }}>{t.commission}{arrow("commission")}</th>
              <th onClick={function() { toggleSort("feeMarkup"); }} style={{ ...th, textAlign: "right" }}>{t.feeMarkup}{arrow("feeMarkup")}</th>
              <th onClick={function() { toggleSort("financeMarkup"); }} style={{ ...th, textAlign: "right" }}>{t.financeMarkup}{arrow("financeMarkup")}</th>
              <th onClick={function() { toggleSort("totalProfit"); }} style={{ ...th, textAlign: "right" }}>{t.totalProfit}{arrow("totalProfit")}</th>
              <th onClick={function() { toggleSort("perBox"); }} style={{ ...th, textAlign: "right" }}>{t.perBox}{arrow("perBox")}</th>
              <th onClick={function() { toggleSort("saleStatus"); }} style={{ ...th, textAlign: "center" }}>Status{arrow("saleStatus")}</th>
              <th onClick={function() { toggleSort("margin"); }} style={{ ...th, textAlign: "right" }}>{t.margin}{arrow("margin")}</th>
            </tr></thead>
            <tbody>{sorted.map(function(d) {
              return <tr key={d.lot} onClick={function() { setSelectedLot(d); }} style={{ cursor: "pointer", borderBottom: "1px solid var(--color-border)", transition: "background 0.15s" }} onMouseEnter={function(e) { e.currentTarget.style.background = "var(--color-bg-hover)"; }} onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}>
                <td style={{ padding: "9px 8px", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: "var(--color-accent)", textAlign: "center" }}>{d.lot}</td>
                <td style={{ padding: "9px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{d.shipDate ? new Date(d.shipDate+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"2-digit",year:"2-digit"}) : "—"}</td>
                <td style={{ padding: "9px 8px", fontSize: 12, fontWeight: 600 }}>{d.shipper}</td>
                <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{d.cases}</td>
                <td style={{ padding: "9px 8px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-muted)" }}>W{d.week}</td>
                <td style={{ padding: "9px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{d.seasonName || "\u2014"}</td>
                <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: clr(d.priceDiff) }}>{fmtMoney(d.priceDiff)}</td>
                <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#059669" }}>{fmtMoney(d.commission)}</td>
                <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: clr(d.feeMarkup) }}>{fmtMoney(d.feeMarkup)}</td>
                <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: clr(d.financeMarkup) }}>{d.financeMarkup !== 0 ? fmtMoney(d.financeMarkup) : "\u2014"}</td>
                <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: clr(d.totalProfit) }}>{fmtMoney(d.totalProfit)}</td>
                <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-accent)" }}>{fmtMoney(d.perBox)}</td>
                <td style={{ padding: "9px 8px", textAlign: "center" }}>{(function(){if(!d.saleStatus)return"—";var colors={goodDelivery:{bg:"rgba(16,185,129,0.12)",text:"#059669",border:"rgba(16,185,129,0.3)"},protection:{bg:"rgba(239,68,68,0.12)",text:"#DC2626",border:"rgba(239,68,68,0.3)"},pas:{bg:"rgba(245,158,11,0.12)",text:"#D97706",border:"rgba(245,158,11,0.3)"}};var sc=colors[d.saleStatus]||{};var lb={goodDelivery:"Good",protection:"Protection",pas:"PAS"};return <span style={{padding:"2px 6px",borderRadius:8,fontSize:8,fontWeight:700,background:sc.bg,color:sc.text,border:"1px solid "+(sc.border||"transparent")}}>{lb[d.saleStatus]||d.saleStatus}</span>})()}</td>
                <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: clr(d.margin) }}>{fmtPct(d.margin)}</td>
              </tr>;
            })}</tbody>
            <tfoot><tr style={{ borderTop: "2px solid var(--color-border)", fontWeight: 700 }}>
              {/* Columns must align with the 14-col header:
                  lot|date|shipper (colSpan=3 TOTAL), cases, week|season (colSpan=2),
                  priceDiff, commission, feeMarkup, financeMarkup, totalProfit, perBox,
                  status (empty), margin. */}
              <td style={{ padding: "10px 8px" }} colSpan={3}>TOTAL</td>
              <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{totals.cases}</td>
              <td colSpan={2}></td>
              <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: clr(totals.priceDiff) }}>{fmtMoney(totals.priceDiff)}</td>
              <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#059669" }}>{fmtMoney(totals.commission)}</td>
              <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: clr(totals.feeMarkup) }}>{fmtMoney(totals.feeMarkup)}</td>
              <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: clr(totals.financeMarkup) }}>{fmtMoney(totals.financeMarkup)}</td>
              <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: clr(totals.profit) }}>{fmtMoney(totals.profit)}</td>
              <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{fmtMoney(totals.perBox)}</td>
              <td></td>
              <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: clr(totals.margin) }}>{fmtPct(totals.margin)}</td>
            </tr></tfoot>
          </table>
        </div>
      )}

      {/* BREAKDOWN MODAL \u2014 DraggableModal per CLAUDE.md "REGLA DE VENTANAS" */}
      {selectedLot && (
        <DraggableModal title={"LOT " + selectedLot.lot + " \u00b7 " + selectedLot.shipper + " \u00b7 W" + selectedLot.week} onClose={function() { setSelectedLot(null); }} initialWidth={Math.min(1200, Math.floor(window.innerWidth * 0.85))} initialHeight={Math.min(900, window.innerHeight - 60)}>
            <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 10 }}>{t.breakdown}</div>
              {/* Summary cards */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
                <div style={{ padding: 12, borderRadius: 8, background: "var(--color-bg)", textAlign: "center" }}><div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: clr(selectedLot.priceDiff) }}>{fmtMoney(selectedLot.priceDiff)}</div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{t.priceDiff}</div></div>
                <div style={{ padding: 12, borderRadius: 8, background: "var(--color-bg)", textAlign: "center" }}><div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "#059669" }}>{fmtMoney(selectedLot.commission)}</div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{t.commission}</div></div>
                <div style={{ padding: 12, borderRadius: 8, background: "var(--color-bg)", textAlign: "center" }}><div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: clr(selectedLot.feeMarkup) }}>{fmtMoney(selectedLot.feeMarkup)}</div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{t.feeMarkup}</div></div>
                <div style={{ padding: 12, borderRadius: 8, background: "rgba(42,122,111,0.06)", border: "1px solid rgba(42,122,111,0.15)", textAlign: "center" }}><div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: clr(selectedLot.totalProfit) }}>{fmtMoney(selectedLot.totalProfit)}</div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{t.totalProfit} ({fmtPct(selectedLot.margin)}) \u00b7 {fmtMoney(selectedLot.perBox)}/{lang === "es" ? "caja" : "box"}</div></div>
              </div>
              {/* Fee detail */}
              {(selectedLot.feeDetail || []).length > 0 && (<>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-muted)", marginBottom: 6 }}>{t.byFee}</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: 16 }}>
                  <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}><th style={{ padding: "4px 6px", textAlign: "left", fontSize: 9, color: "var(--color-text-muted)" }}>{t.feeName}</th><th style={{ padding: "4px 6px", textAlign: "left", fontSize: 9, color: "var(--color-text-muted)" }}>{t.category}</th><th style={{ padding: "4px 6px", textAlign: "right", fontSize: 9, color: "var(--color-text-muted)" }}>{t.chargedToShipper}</th><th style={{ padding: "4px 6px", textAlign: "right", fontSize: 9, color: "var(--color-text-muted)" }}>{t.actualCost}</th><th style={{ padding: "4px 6px", textAlign: "right", fontSize: 9, color: "var(--color-text-muted)" }}>{t.feeProfit}</th></tr></thead>
                  <tbody>{selectedLot.feeDetail.map(function(fd, i) { var p = fd.charged - fd.cost; return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}><td style={{ padding: "5px 6px" }}>{fd.name}</td><td style={{ padding: "5px 6px", color: "var(--color-text-secondary)" }}>{categoryLabel(feeCategoryByName[fd.name] || "other")}</td><td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(fd.charged)}</td><td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{fd.cost > 0 ? fmtMoney(fd.cost) : "\u2014"}</td><td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: clr(p) }}>{fmtMoney(p)}</td></tr>; })}</tbody>
                </table>
              </>)}
              {/* Product detail */}
              {(selectedLot.products || []).length > 0 && (<>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-muted)", marginBottom: 6 }}>{t.byProduct}</div>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-muted)", marginBottom: 6, marginTop: 12 }}></div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}><th style={{ padding: "4px 6px", textAlign: "left", fontSize: 9, color: "var(--color-text-muted)" }}>{t.product}</th><th style={{ padding: "4px 6px", textAlign: "right", fontSize: 9, color: "var(--color-text-muted)" }}>{t.priceDiff}</th></tr></thead>
                  <tbody>{selectedLot.products.map(function(p, i) { return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}><td style={{ padding: "5px 6px", fontWeight: 600 }}>{p.product}</td><td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: clr(p.priceDiff) }}>{fmtMoney(p.priceDiff)}</td></tr>; })}</tbody>
                </table>
              </>)}
            </div>
        </DraggableModal>
      )}

      {/* BY PRODUCT */}
      {view === "byProduct" && (
        <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid var(--color-border)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>{t.product}</th><th style={{ ...th, textAlign: "right" }}>{t.priceDiff}</th><th style={{ ...th, textAlign: "right" }}>{lang === "es" ? "Embarques" : "Shipments"}</th></tr></thead>
            <tbody>{productAgg.map(function(p) { return <tr key={p.product} style={{ borderBottom: "1px solid var(--color-border)" }}><td style={{ padding: "9px 8px", fontSize: 12, fontWeight: 600 }}>{p.product}</td><td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: clr(p.priceDiff) }}>{fmtMoney(p.priceDiff)}</td><td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{p.count}</td></tr>; })}</tbody>
          </table>
        </div>
      )}

      {/* BY FEE */}
      {view === "byFee" && (
        <div>
          {/* Category filter — multi-select, scoped to this tab. */}
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <div style={{ minWidth: 200, maxWidth: 280 }}><SearchSelect multi value={feeCatFilter} onChange={v => setFeeCatFilter(v)} placeholder={t.allCategories} options={FEE_CATEGORY_KEYS.map(function(c) { return { value: c, label: categoryLabel(c) }; })} /></div>
          </div>
          <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid var(--color-border)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th onClick={function() { toggleFeeSort("category"); }} style={th}>{t.category}{feeArrow("category")}</th>
                <th onClick={function() { toggleFeeSort("name"); }} style={th}>{t.feeName}{feeArrow("name")}</th>
                <th onClick={function() { toggleFeeSort("charged"); }} style={{ ...th, textAlign: "right" }}>{t.chargedToShipper}{feeArrow("charged")}</th>
                <th onClick={function() { toggleFeeSort("actual"); }} style={{ ...th, textAlign: "right" }}>{t.actualCost}{feeArrow("actual")}</th>
                <th onClick={function() { toggleFeeSort("profit"); }} style={{ ...th, textAlign: "right" }}>{t.feeProfit}{feeArrow("profit")}</th>
              </tr></thead>
              <tbody>{feeSorted.map(function(f) { var profit = f.charged - f.actual; return <tr key={f.name} style={{ borderBottom: "1px solid var(--color-border)" }}><td style={{ padding: "9px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{categoryLabel(f.category)}</td><td style={{ padding: "9px 8px", fontSize: 12, fontWeight: 600 }}>{f.name}</td><td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(f.charged)}</td><td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{fmtMoney(f.actual)}</td><td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: clr(profit) }}>{fmtMoney(profit)}</td></tr>; })}</tbody>
              {/* TOTAL row reduces over feeSorted (the visible rows after
                  category filter), so the column sums match what's shown.
                  Markup Profit = sum of (charged − actual) per row — see
                  earlier note for why this beats totals.feeMarkup. */}
              <tfoot><tr style={{ borderTop: "2px solid var(--color-border)", fontWeight: 700 }}>
                <td style={{ padding: "10px 8px" }} colSpan={2}>TOTAL</td>
                <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(feeSorted.reduce(function(s, f) { return s + f.charged; }, 0))}</td>
                <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{fmtMoney(feeSorted.reduce(function(s, f) { return s + f.actual; }, 0))}</td>
                <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: clr(feeSorted.reduce(function(s, f) { return s + (f.charged - f.actual); }, 0)) }}>{fmtMoney(feeSorted.reduce(function(s, f) { return s + (f.charged - f.actual); }, 0))}</td>
              </tr></tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}